'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { createPortal } from 'react-dom';

export interface GalleryPhoto {
  src: string;
  alt: string;
  width: number;
  height: number;
}

/**
 * One project's photos as a horizontal, scrollable strip (snap-scrolling, arrow buttons on
 * desktop). Thumbnails grow slightly on hover; clicking opens a full-screen viewer with
 * previous / next, keyboard (← → Esc) and swipe-free tap controls. Images are never shown
 * larger than their real size.
 */
export default function RenderGallery({ photos, title }: { photos: GalleryPhoto[]; title: string }) {
  const strip = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState<number | null>(null);

  const scrollBy = (dir: 1 | -1) => strip.current?.scrollBy({ left: dir * strip.current.clientWidth * 0.8, behavior: 'smooth' });

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback((d: number) => setOpen((i) => (i === null ? i : (i + d + photos.length) % photos.length)), [photos.length]);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close, step]);

  const current = open !== null ? photos[open] : null;

  return (
    <div className="relative">
      <ul
        ref={strip}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory list-none m-0 p-0 pb-3"
        style={{ scrollbarWidth: 'thin' }}
        aria-label={`${title} — ${photos.length} designs`}
      >
        {photos.map((p, i) => (
          <li key={p.src} className="snap-start flex-shrink-0">
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="group block overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
              aria-label={`Enlarge: ${p.alt}`}
            >
              {/* Fixed height, width from the photo's own shape — no crop, no letterbox */}
              <div className="relative h-[180px] sm:h-[220px]" style={{ aspectRatio: `${p.width} / ${p.height}` }}>
                <Image
                  src={p.src}
                  alt={p.alt}
                  fill
                  sizes="(min-width: 640px) 300px, 240px"
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </button>
          </li>
        ))}
      </ul>

      {/* Desktop scroll arrows */}
      {photos.length > 3 && (
        <>
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            className="hidden md:flex absolute left-2 top-[100px] -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full shadow"
            style={{ backgroundColor: 'rgba(250,248,245,0.92)', color: 'var(--foreground)' }}
            aria-label={`Scroll ${title} left`}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            className="hidden md:flex absolute right-2 top-[100px] -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full shadow"
            style={{ backgroundColor: 'rgba(250,248,245,0.92)', color: 'var(--foreground)' }}
            aria-label={`Scroll ${title} right`}
          >
            ›
          </button>
        </>
      )}

      {/* Full-screen viewer — rendered into <body> so animated (transformed) parents can't trap it */}
      {current && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10"
          style={{ backgroundColor: 'rgba(15,13,11,0.92)' }}
          role="dialog"
          aria-modal="true"
          aria-label={current.alt}
          onClick={close}
        >
          <figure className="relative max-w-full max-h-full m-0" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={current.src}
              alt={current.alt}
              className="block max-w-full object-contain"
              style={{ maxHeight: 'calc(100vh - 140px)', width: 'auto', height: 'auto', maxWidth: `min(100%, ${current.width}px)` }}
            />
            <figcaption className="mt-3 text-sm text-center" style={{ color: '#D8D2CA' }}>
              {title} · {open! + 1} / {photos.length}
            </figcaption>
          </figure>
          <button type="button" onClick={close} className="absolute top-4 right-5 text-3xl leading-none" style={{ color: '#FAF8F5' }} aria-label="Close">
            ×
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full text-2xl"
            style={{ backgroundColor: 'rgba(250,248,245,0.15)', color: '#FAF8F5' }}
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full text-2xl"
            style={{ backgroundColor: 'rgba(250,248,245,0.15)', color: '#FAF8F5' }}
            aria-label="Next"
          >
            ›
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
